import { Alert, AlertProps, AlertTitle, Button, Stack } from '@mui/material'
import { RuuviTag } from '../../types'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { updateRuuviTag } from '../../services/ruuviTagService'
import { LoadingButton } from '@mui/lab'
import { useDispatch } from 'react-redux'
import {
  createErrorNotification,
  createSuccessNotification
} from '../../redux/slices/notifications'

export interface RuuviTagUninstallAlertProps extends Omit<AlertProps, 'severity' | 'children'> {
  ruuviTag: RuuviTag
  onUpdate?: (updated: RuuviTag) => void
  onCancel?: () => void
}

export default function RuuviTagUninstallAlert({
  ruuviTag,
  onUpdate,
  onCancel,
  ...props
}: RuuviTagUninstallAlertProps) {
  const { t } = useTranslation('installation')

  const dispatch = useDispatch()

  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  async function handleSubmit() {
    try {
      setSubmitting(true)

      const updated = await updateRuuviTag(ruuviTag.id, {
        asset: {
          name: ruuviTag.id,
          description: null,
          project: null,
          company: ruuviTag?.asset?.companyId ?? null,
          level: null,
          lift: null
        }
      })

      dispatch(createSuccessNotification(t('uninstallRuuviTagSuccess')))

      onUpdate?.(updated)
    } catch {
      dispatch(createErrorNotification(t('uninstallRuuviTagError')))
    } finally {
      setSubmitting(false)
    }
  }

  if (ruuviTag?.asset?.projectId !== undefined && ruuviTag?.asset?.projectId === null) {
    return null
  }

  return (
    <Alert severity="warning" {...props}>
      <AlertTitle sx={{ mt: 0 }}>
        {t('ruuviTagMustBeUninstalledBeforeReinstall', {
          project: ruuviTag.asset?.project?.name ?? `#${ruuviTag?.asset?.projectId}`
        })}
      </AlertTitle>

      <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 2 }}>
        <LoadingButton
          variant="contained"
          color="warning"
          size="small"
          loading={isSubmitting}
          onClick={handleSubmit}
        >
          {t('actions:uninstall')}
        </LoadingButton>
        <Button
          variant="outlined"
          color="inherit"
          size="small"
          disabled={isSubmitting}
          onClick={onCancel}
        >
          {t('actions:cancel')}
        </Button>
      </Stack>
    </Alert>
  )
}
